import type { CSSProperties } from "react";

import { DROPDOWN_TRANSFORM } from "./constants";

interface SurfaceAnimationInput {
  isAnimating: boolean;
  isClosing: boolean;
}

interface SurfaceDirectionInput extends SurfaceAnimationInput {
  opensUpwards: boolean;
}

export function buildDropdownWrapperStyle(
  dropdownListStyle: CSSProperties,
): CSSProperties {
  return {
    ...dropdownListStyle,
    padding: "12px",
  };
}

export function buildAmbientGlowStyle(
  input: SurfaceAnimationInput,
): CSSProperties {
  return {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "100%",
    height: "100%",
    backgroundImage: "radial-gradient(circle at center, rgba(102, 254, 193, 0.15) 0%, transparent 70%)",
    filter: "blur(20px)",
    opacity: input.isAnimating && !input.isClosing ? 1 : 0,
    transition: "opacity 300ms ease-out",
    pointerEvents: "none",
    zIndex: -1,
  };
}

export function buildDropdownContainerStyle(
  input: SurfaceDirectionInput,
): CSSProperties {
  return {
    opacity: input.isAnimating && !input.isClosing ? 1 : 0,
    transform: resolveContainerTransform(input),
    transition:
      "opacity 250ms cubic-bezier(0.2, 0.8, 0.2, 1), transform 350ms cubic-bezier(0.2, 0.8, 0.2, 1)",
    perspective: "1000px",
    position: "relative",
    overflow: "hidden",
  };
}

export function buildTopAccentStyle(
  input: SurfaceDirectionInput,
): CSSProperties {
  return {
    position: "absolute",
    ...resolveTopAccentEdge(input.opensUpwards),
    left: "10%",
    right: "10%",
    height: "2px",
    backgroundColor: "transparent",
    backgroundImage: "linear-gradient(90deg, transparent, rgba(102, 254, 193, 0.8), transparent)",
    boxShadow: input.opensUpwards
      ? "0 -1px 12px 1px rgba(102, 254, 193, 0.4)"
      : "0 1px 12px 1px rgba(102, 254, 193, 0.4)",
    opacity: input.isAnimating && !input.isClosing ? 1 : 0,
    transition: "opacity 400ms ease 100ms",
  };
}

export function resolveOptionsAnimate(
  optionsViewportHeight: number | null,
): { height: number } | undefined {
  if (optionsViewportHeight === null) {
    return undefined;
  }

  return {
    height: optionsViewportHeight,
  };
}

export function buildScrollableStyle(input: {
  optionsViewportHeight: number | null;
  maxHeight: number | undefined;
}): CSSProperties {
  return {
    width: "100%",
    ...resolveViewportHeightStyle(input.optionsViewportHeight),
    ...resolveMaxHeightStyle(input.maxHeight),
  };
}

function resolveContainerTransform(input: SurfaceDirectionInput): string {
  if (input.isAnimating && !input.isClosing) {
    return DROPDOWN_TRANSFORM.Open;
  }

  return input.opensUpwards
    ? DROPDOWN_TRANSFORM.ClosedUpwards
    : DROPDOWN_TRANSFORM.ClosedDownwards;
}

function resolveTopAccentEdge(opensUpwards: boolean): CSSProperties {
  return opensUpwards ? { bottom: 0 } : { top: 0 };
}

function resolveViewportHeightStyle(
  optionsViewportHeight: number | null,
): CSSProperties {
  return optionsViewportHeight !== null ? { height: "100%" } : {};
}

function resolveMaxHeightStyle(maxHeight: number | undefined): CSSProperties {
  return maxHeight ? { maxHeight: `${maxHeight}px` } : {};
}
